import { Component, OnDestroy } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, Observable, of, Subject, take } from 'rxjs';
import { InvalidFieldComponent } from '../../../../shared/components/invalid-field/invalid-field.component';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { FORM_SECTION_DESCRIPTIONS } from '../../const/form-help-text.const';
import { AppForm, FaqURLType } from '../../models/app-form';
import { LegalAndTermsFormErrorJSON } from './models/legal-terms';
import { formLegalAndTermsErrors } from './utils/form-errors';

@Component({
    selector: 'app-legal-and-terms-section',
    imports: [
        ReactiveFormsModule,
        MatExpansionModule,
        MatInputModule,
        MatProgressSpinnerModule,
        InvalidFieldComponent,
        SectionHeaderComponent,
    ],
    templateUrl: './legal-and-terms-section.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class LegalAndTermsSectionComponent implements OnDestroy {
    public legalAndTermsErrors: LegalAndTermsFormErrorJSON;
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.legalAndInfoUrls;
    public validatingFaqURL: boolean = false;
    public faqURLType: FaqURLType = FaqURLType.EMPTY;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private parent: FormGroupDirective,
        private http: HttpClient
    ) {
        this.legalAndTermsErrors = formLegalAndTermsErrors;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    get privacyUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.privacyUrl;
    }

    get termsUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.termsUrl;
    }

    get faqUrlControl(): FormControl<string | null> {
        return this.parentForm?.controls?.faqURL;
    }

    getFaqURLType(): Observable<FaqURLType> {
        const url = this.faqUrlControl.value;

        if (!url) {
            return of(FaqURLType.EMPTY);
        }

        return this.http.head(url, { observe: 'response' }).pipe(
            map((response) => {
                const contentType = response.headers.get('Content-Type') || '';
                return contentType.includes('application/pdf') ? FaqURLType.PDF : FaqURLType.WEBSITE;
            })
        );
    }

    validateFaqURL(): void {
        if (this.validatingFaqURL || !this.faqUrlControl.value || this.faqUrlControl.invalid) {
            return;
        }

        this.faqUrlControl.disable();
        this.validatingFaqURL = true;

        this.getFaqURLType()
            .pipe(take(1))
            .subscribe({
                next: (faqURLType) => {
                    this.faqURLType = faqURLType;
                    this.faqUrlControl.enable();
                    this.validatingFaqURL = false;
                },
                error: () => {
                    this.validatingFaqURL = false;
                    this.faqUrlControl.enable();
                    const faqURL: string | null = this.faqUrlControl.value;

                    if (faqURL) {
                        if (faqURL.toLowerCase().endsWith('.pdf')) {
                            this.faqURLType = FaqURLType.PDF;
                        } else {
                            this.faqURLType = FaqURLType.WEBSITE;
                        }
                    }
                },
            });
    }

    updateFaqURLType(faqURL: string | null | undefined, pdfURL: string | null | undefined): void {
        if (faqURL?.length) {
            this.faqURLType = FaqURLType.WEBSITE;
        }

        if (pdfURL?.length) {
            this.faqURLType = FaqURLType.PDF;
        }
    }
}
